import { Alert, Button, Form, Input, InputNumber, Modal, Table, Tag, message } from "antd";
import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/slices/auth.slice";
import {
  connectWhatsappAccount,
  createWalletRechargeOrder,
  fetchMyWhatsappAccount,
  fetchWalletBalance,
  fetchWalletStatement,
  verifyWalletRechargePayment,
  whatsappError,
} from "../../services/whatsapp.service";

const STATUS_COLOR = {
  connected: "green",
  pending: "orange",
  disabled: "red",
};

const WALLET_LEDGER_COLOR = {
  RECHARGE: "green",
  DEBIT: "red",
  REFUND: "blue",
  ADJUSTMENT: "orange",
};

/** Same 60px-ish loader pattern already used for the Razorpay checkout in
 * the (currently disabled) parent fee-payment flow — ViewFeeDetails.js. */
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

/**
 * Manual credential entry, mirroring the existing Razorpay KYC flow — the
 * school's own Meta admin generates these values and pastes them in. Every
 * school connects its own WhatsApp Business Account; nothing here is shared
 * across schools.
 */
const WhatsAppAccountSettings = () => {
  const queryClient = useQueryClient();
  const user = useSelector(selectUser);
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [rechargeForm] = Form.useForm();
  const [recharging, setRecharging] = useState(false);
  const [statementPage, setStatementPage] = useState(1);

  const account = useQuery("whatsapp/account/mine", fetchMyWhatsappAccount);
  const data = account?.data?.data;
  const connected = account?.data?.connected;

  const wallet = useQuery("whatsapp-wallet/balance", fetchWalletBalance);
  const walletBalancePaise = wallet?.data?.balance_paise ?? 0;

  const statement = useQuery(
    ["whatsapp-wallet/statement", statementPage],
    () => fetchWalletStatement({ page: statementPage, limit: 10 }),
  );

  useEffect(() => {
    if (connected && data && !editing) {
      form.setFieldsValue({
        waba_id: data.waba_id,
        phone_number_id: data.phone_number_id,
        template_name_none: data.template_name_none,
        template_name_image: data.template_name_image,
        template_name_video: data.template_name_video,
        template_name_document: data.template_name_document,
        template_lang: data.template_lang,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, connected]);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      await connectWhatsappAccount(values);
      message.success("WhatsApp Business Account connected");
      setEditing(false);
      form.setFieldValue("access_token", undefined);
      queryClient.invalidateQueries("whatsapp/account/mine");
    } catch (err) {
      message.error(whatsappError(err, "Could not connect the account"));
    } finally {
      setSaving(false);
    }
  };

  const handleRecharge = async (values) => {
    setRecharging(true);
    try {
      const amountPaise = Math.round(Number(values.amount) * 100);
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        message.error("Razorpay SDK failed to load. Are you online?");
        setRecharging(false);
        return;
      }

      const schoolId = user?.school?._id || user?.school;
      const order = await createWalletRechargeOrder(amountPaise, schoolId);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: "is-kool WhatsApp Wallet",
        description: "WhatsApp wallet recharge",
        order_id: order.id,
        handler: async (response) => {
          try {
            await verifyWalletRechargePayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            message.success("Wallet recharged successfully");
            setRechargeOpen(false);
            rechargeForm.resetFields();
            queryClient.invalidateQueries("whatsapp-wallet/balance");
            queryClient.invalidateQueries("whatsapp-wallet/statement");
          } catch (err) {
            message.error(
              whatsappError(err, "Payment verification failed. Please contact support."),
            );
          } finally {
            setRecharging(false);
          }
        },
        modal: {
          ondismiss: () => setRecharging(false),
        },
        theme: { color: "#25D366" },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (err) {
      message.error(whatsappError(err, "Could not start the recharge"));
      setRecharging(false);
    }
  };

  return (
    <div className="mt-8">
      <div
        style={{ borderRadius: "10px", color: "#616161" }}
        className="bg-white drop-shadow-md p-8 w-full"
      >
        <div className="text-2xl text-orange-500 font-semibold">
          WhatsApp Business Account
        </div>

        <Alert
          className="mt-4 mb-2"
          type="info"
          showIcon
          message="Your school connects its own WhatsApp Business Account. Generate these values in your Meta Business Settings, then paste them here — is-kool never creates or stores them on Meta's side for you."
        />

        {account?.isLoading ? null : connected && !editing ? (
          <div className="pt-6">
            <div className="flex items-center gap-3 pb-4">
              <Tag color={STATUS_COLOR[data?.status] || "default"}>
                {(data?.status || "unknown").toUpperCase()}
              </Tag>
              <span className="text-sm text-gray-500">
                Access token •••• {data?.access_token_last4}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-400">WABA ID</div>
                <div>{data?.waba_id}</div>
              </div>
              <div>
                <div className="text-gray-400">Phone Number ID</div>
                <div>{data?.phone_number_id}</div>
              </div>
              <div>
                <div className="text-gray-400">Templates</div>
                <div>
                  {data?.template_name_none}, {data?.template_name_image},{" "}
                  {data?.template_name_video}, {data?.template_name_document}
                </div>
              </div>
              <div>
                <div className="text-gray-400">Daily conversation limit</div>
                <div>
                  {data?.daily_conversation_cap ?? "—"} per 24 hours
                  <div className="text-xs text-gray-400">
                    Contact support to raise this once Meta upgrades your tier.
                  </div>
                </div>
              </div>
            </div>

            <Button className="mt-6" onClick={() => setEditing(true)}>
              Replace credentials
            </Button>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            className="pt-8"
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 32, rowGap: 16 }}>
              <Form.Item
                label="WABA ID"
                name="waba_id"
                rules={[{ required: true, message: "WABA ID is required" }]}
              >
                <Input placeholder="WhatsApp Business Account ID" />
              </Form.Item>
              <Form.Item
                label="Phone Number ID"
                name="phone_number_id"
                rules={[{ required: true, message: "Phone Number ID is required" }]}
              >
                <Input placeholder="From API Setup, not the phone number itself" />
              </Form.Item>
            </div>

            <Form.Item
              className="mt-2"
              label="Access Token"
              name="access_token"
              rules={[
                {
                  required: !connected,
                  message: "Access token is required",
                },
              ]}
            >
              <Input.Password
                placeholder={
                  connected
                    ? `Currently •••• ${data?.access_token_last4} — leave blank to keep it, or paste a new one`
                    : "System User permanent token"
                }
              />
            </Form.Item>

            <div className="text-sm text-gray-500 mt-4 mb-3">
              Four approved templates, one per header format:
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 32, rowGap: 16 }}>
              <Form.Item
                label="Template — text only"
                name="template_name_none"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Template — image header"
                name="template_name_image"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Template — video header"
                name="template_name_video"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Template — document header (also carries audio)"
                name="template_name_document"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input />
              </Form.Item>
            </div>

            <Form.Item
              className="mt-2"
              label="Template Language"
              name="template_lang"
              initialValue="en"
            >
              <Input placeholder="en" />
            </Form.Item>

            <div className="flex gap-4 justify-center pt-6">
              <Button size="large" type="primary" htmlType="submit" loading={saving}>
                {connected ? "Save changes" : "Connect WhatsApp"}
              </Button>
              {connected && (
                <Button size="large" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              )}
            </div>
          </Form>
        )}
      </div>

      <div
        style={{ borderRadius: "10px", color: "#616161", marginTop: 32 }}
        className="bg-white drop-shadow-md p-8 w-full"
      >
        <div className="text-2xl text-orange-500 font-semibold">
          WhatsApp Wallet
        </div>
        <div className="text-sm text-gray-500 mt-1">
          Every WhatsApp message sent costs ₹0.15 — Marketing/Campaign group
          messages cost ₹0.20. Recharge below to top up your school's balance.
        </div>

        <div className="flex items-center justify-between pt-6">
          <div>
            <div className="text-gray-400 text-sm">Current balance</div>
            <div
              className="inline-block mt-1 px-4 py-2 rounded-lg text-3xl font-bold"
              style={{
                color: walletBalancePaise > 0 ? "#128C7E" : "#cf1322",
                backgroundColor: walletBalancePaise > 0 ? "#E7F9F3" : "#fff1f0",
              }}
            >
              {wallet?.isLoading
                ? "…"
                : `₹${(walletBalancePaise / 100).toFixed(2)}`}
            </div>
          </div>
          <Button
            size="large"
            type="primary"
            onClick={() => setRechargeOpen(true)}
            disabled={!connected}
            title={!connected ? "Connect your WhatsApp Business Account first" : undefined}
          >
            Recharge
          </Button>
        </div>

        <div className="mt-8">
          <div className="text-sm text-gray-500 mb-2">Statement</div>
          <Table
            size="small"
            rowKey="_id"
            loading={statement?.isLoading}
            dataSource={statement?.data?.data || []}
            pagination={{
              current: statementPage,
              pageSize: 10,
              total: statement?.data?.total || 0,
              onChange: (page) => setStatementPage(page),
            }}
            columns={[
              {
                title: "Type",
                dataIndex: "type",
                render: (type) => (
                  <Tag color={WALLET_LEDGER_COLOR[type] || "default"}>{type}</Tag>
                ),
              },
              {
                title: "Amount",
                dataIndex: "amount_paise",
                render: (paise) => `₹${(paise / 100).toFixed(2)}`,
              },
              {
                title: "Balance after",
                dataIndex: "balance_after_paise",
                render: (paise) => `₹${(paise / 100).toFixed(2)}`,
              },
              {
                title: "Date",
                dataIndex: "createdAt",
                render: (d) => (d ? new Date(d).toLocaleString() : "—"),
              },
              { title: "Note", dataIndex: "note" },
            ]}
          />
        </div>
      </div>

      <Modal
        title="Recharge WhatsApp Wallet"
        open={rechargeOpen}
        onCancel={() => {
          setRechargeOpen(false);
          rechargeForm.resetFields();
        }}
        footer={null}
      >
        <Form form={rechargeForm} layout="vertical" onFinish={handleRecharge}>
          <Alert
            className="mb-4"
            type="info"
            showIcon
            message="You are about to make a secure online payment via Razorpay."
          />
          <Form.Item
            label="Amount (₹)"
            name="amount"
            rules={[
              { required: true, message: "Enter an amount" },
              {
                type: "number",
                min: 1,
                message: "Amount must be at least ₹1",
              },
            ]}
          >
            <InputNumber className="w-full" min={1} placeholder="e.g. 500" />
          </Form.Item>
          <Button
            className="w-full"
            size="large"
            type="primary"
            htmlType="submit"
            loading={recharging}
          >
            Pay & Recharge
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default WhatsAppAccountSettings;
